import type {HydratedAPITask} from '@github-ui/agent-sessions/types/api-task'
import type {Session} from '@github-ui/agent-sessions/types/session'
import type {AhpChatStartRequest} from '@github-ui/ahp-session-client/contracts'
import {resolveTaskOwnerLogin} from '@github-ui/copilot-shared-ui/utils/task-owner'

type TaskEnvironment = NonNullable<HydratedAPITask['current_environment']>
export type WorkbenchEnvironmentKind = TaskEnvironment['kind']

export function resolveWorkbenchOwner(
  task: Pick<HydratedAPITask, 'compute' | 'owner' | 'creator'>,
  repositoryOwner?: string,
  launchOwner?: string,
): string | undefined {
  return resolveTaskOwnerLogin(task, repositoryOwner) || launchOwner
}

export function resolveWorkbenchEnvironmentKind({
  taskEnvironment,
  boundEnvironmentId,
  resolvedEnvironment,
  launchEnvironmentKind,
}: {
  taskEnvironment?: TaskEnvironment | null
  boundEnvironmentId?: string
  resolvedEnvironment?: TaskEnvironment | null
  launchEnvironmentKind?: WorkbenchEnvironmentKind
}): WorkbenchEnvironmentKind | undefined {
  if (boundEnvironmentId && resolvedEnvironment?.id === boundEnvironmentId) return resolvedEnvironment.kind
  if (taskEnvironment) {
    return !boundEnvironmentId || taskEnvironment.id === boundEnvironmentId ? taskEnvironment.kind : undefined
  }
  return launchEnvironmentKind
}

export function provisioningWorkbenchOwner(request: AhpChatStartRequest): string | undefined {
  const target = request.provisioningTarget
  if (target?.kind === 'scope') return target.owner
  if (target?.kind === 'space') return target.space.owner
  if (target?.kind === 'repository') return target.nameWithOwner.split('/')[0] || target.scopeOwner
  return request.metadata?.scope ?? request.metadata?.space?.owner
}

type WorkbenchSession = Pick<Session, 'id' | 'created_at' | 'environment_id'>

/**
 * Picks the session whose environment gates the workbench, matching how the task
 * chat resolves its active session: an explicit `session_id` wins, otherwise the
 * newest session by creation time. Callers pass the sessions resolved by
 * `useSessions` rather than the task payload's array, because a deep link can
 * reference a session the task array does not contain, and neither source
 * guarantees a creation order.
 */
export function resolveWorkbenchSession<T extends WorkbenchSession>(
  sessions: readonly T[],
  selectedSessionId?: string | null,
): T | undefined {
  if (selectedSessionId) return sessions.find(session => session.id === selectedSessionId)
  return sessions.reduce<T | undefined>(
    (newest, session) =>
      !newest || new Date(session.created_at).getTime() >= new Date(newest.created_at).getTime() ? session : newest,
    undefined,
  )
}
